/**
 * Typed client for the booking API.
 *
 * The API speaks camelCase, so these types mirror its wire shapes rather than
 * its Python models. Amounts are AED — the currency actually charged.
 */

const BASE = (import.meta.env.VITE_API_BASE ?? '').replace(/\/$/, '');

const TOKEN_KEY = 'psd.admin.token';

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* private mode */
  }
}

export class Unauthorised extends Error {}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  if (!BASE) throw new Error('VITE_API_BASE is not set');
  const token = getToken();
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      'content-type': 'application/json',
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...(init?.headers ?? {}),
    },
  });
  if (res.status === 401) {
    setToken(null);
    throw new Unauthorised('Session expired');
  }
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { detail?: string } | null;
    throw new Error(body?.detail ?? `${res.status} ${res.statusText}`);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

// --- Types ------------------------------------------------------------------

export interface Product {
  sku: string;
  name: string;
  active: boolean;
  /** null means the real rate is not confirmed; the site renders "AED ___". */
  priceAed: number | null;
  /** The per-guest half of a two-part rate (VIP). null = per vehicle only. */
  pricePerGuestAed: number | null;
  priceUnit: 'car' | 'person' | 'group' | 'night';
  priceIsFrom: boolean;
  priceConfirmed: boolean;
  maxGuestsPerVehicle: number;
  depositPercent: number;
  freeCancellationHours: number;
  durationMinutes: number | null;
}

export interface PriceChange {
  at: string;
  by: string;
  fromAed: number | null;
  toAed: number | null;
  deployHook: string | null;
}

export interface Slot {
  id: number;
  sku: string;
  date: string;
  startsAt: string;
  vehiclesTotal: number;
  vehiclesHeld: number;
  vehiclesAvailable: number;
  seatsPerVehicle: number;
  blocked: boolean;
  blockedReason: string | null;
}

export interface Assignment {
  position: number;
  vehicle: string | null;
  guide: string | null;
  pickupNote: string | null;
}

export interface Booking {
  ref: string;
  status: 'pending' | 'confirmed' | 'cancelled' | 'no_show' | 'completed';
  sku: string;
  productName: string;
  date: string;
  startsAtLocal: string;
  vehicles: number;
  guests: number | null;
  /** Under-3s. Free, but they take a seat — dispatch sizes the convoy for them. */
  infants: number;
  locale: 'en' | 'ar' | 'ru' | 'de';
  /** Where the guest is staying. Abu Dhabi carries a per-vehicle pickup fee. */
  pickupEmirate: 'dubai' | 'sharjah' | 'abu_dhabi';
  paymentMode: 'deposit' | 'full';
  /** The automatic last-minute discount given, and the pickup fee charged. */
  discountAed: number;
  pickupFeeAed: number;
  totalAed: number;
  paidAed: number;
  balanceAed: number;
  freeCancellationUntil: string;
  freeCancellationOpen: boolean;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  pickupAddress: string;
  notes: string | null;
  preferences: Record<string, unknown>;
  assignments: Assignment[];
  /** Chosen add-ons as charged — what dispatch has to arrange. */
  addOns: { sku: string; name: string; quantity: number; lineAed: number }[];
  createdAt: string;
}

export interface Coupon {
  code: string;
  description: string;
  /** Exactly one of these two is set. */
  percentOff: number | null;
  amountOffAed: number | null;
  active: boolean;
  expiresAt: string | null;
  maxRedemptions: number | null;
  /** Paid bookings, not attempts — an abandoned checkout spends nothing. */
  timesRedeemed: number;
  createdAt: string;
}

export interface Health {
  ok: boolean;
  environment: string;
  charge_currency: string;
  availability_provider: string;
  stripe_configured: boolean;
  deploy_hook_configured: boolean;
  notifications_configured: boolean;
}

// --- Calls ------------------------------------------------------------------

export const api = {
  health: () => call<Health>('/health'),

  login: (email: string, password: string) =>
    call<{ accessToken: string; expiresIn: number }>('/v1/admin/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),

  products: () => call<Product[]>('/v1/admin/products'),

  /**
   * Writing a price also fires the Cloudflare Pages deploy hook, because the
   * figure lives in the built HTML. The UI says so before you submit.
   */
  setPrice: (sku: string, priceAed: number | null, pricePerGuestAed: number | null, reason: string) =>
    call<Product>(`/v1/admin/products/${encodeURIComponent(sku)}/price`, {
      method: 'PUT',
      body: JSON.stringify({ priceAed, pricePerGuestAed, reason }),
    }),

  coupons: () => call<Coupon[]>('/v1/admin/coupons'),

  createCoupon: (body: {
    code: string;
    description: string;
    percentOff: number | null;
    amountOffAed: number | null;
    expiresAt: string | null;
    maxRedemptions: number | null;
  }) => call<Coupon>('/v1/admin/coupons', { method: 'POST', body: JSON.stringify(body) }),

  deactivateCoupon: (code: string) =>
    call<Coupon>(`/v1/admin/coupons/${encodeURIComponent(code)}/deactivate`, { method: 'POST' }),

  priceHistory: (sku: string) =>
    call<PriceChange[]>(`/v1/admin/products/${encodeURIComponent(sku)}/price-history`),

  slots: (from: string, to: string, sku?: string) =>
    call<Slot[]>(`/v1/admin/slots?from=${from}&to=${to}${sku ? `&sku=${encodeURIComponent(sku)}` : ''}`),

  upsertSlot: (slot: {
    sku: string;
    date: string;
    startsAt: string;
    vehiclesTotal: number;
    seatsPerVehicle?: number;
    blocked?: boolean;
    blockedReason?: string | null;
  }) => call<Slot>('/v1/admin/slots', { method: 'PUT', body: JSON.stringify(slot) }),

  bookings: (params: { from?: string; to?: string; status?: string }) => {
    const qs = new URLSearchParams();
    if (params.from) qs.set('from', params.from);
    if (params.to) qs.set('to', params.to);
    if (params.status) qs.set('status', params.status);
    return call<Booking[]>(`/v1/admin/bookings?${qs}`);
  },

  assign: (ref: string, body: { position: number; vehicleId?: number; guideId?: number; pickupNote?: string }) =>
    call<Booking>(`/v1/admin/bookings/${encodeURIComponent(ref)}/assign`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  drainNotifications: () =>
    call<{ delivered: number; failed: number }>('/v1/admin/notifications/drain', { method: 'POST' }),
};

export const aed = (v: number | null): string =>
  v === null ? 'AED ___' : `AED ${new Intl.NumberFormat('en-GB', { maximumFractionDigits: 2 }).format(v)}`;

export const today = (): string => new Date().toISOString().slice(0, 10);

export const plusDays = (iso: string, days: number): string => {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};
